import React from "react";

import { Divider } from "@material-ui/core";
import Grid from "@material-ui/core/Grid";
import { useField } from "formik";
import { useSelector } from "react-redux";

import { BankingDetailField } from "components/Form/Fields/BankingDetailField";
import {
  CheckBox,
  DateInput,
  flattenOptions,
  Input,
  SelectField,
  withNamespace,
} from "components/Form/FormikControls";
import { HelpText } from "components/Form/FormikControls/HelpText";
import { SelectFieldOption } from "components/Form/FormikControls/types";
import { Link } from "components/Link";

import { BillingNumberPrefixes } from "constants/billing";
import {
  BillingRunStatus,
  BillingRunStatusOptions,
} from "constants/billingRuns";

import { getSettingsRoute } from "lib/navigation";

import {
  getBillingSettingsByBillingRunId,
  getDefaultBillingSettings,
  selectRuleBookOptions,
  selectSaleOptionsV2,
} from "selectors";

interface BillingRunFormProps {
  billingRunId?: string;
  namespace?: string;
}

export function BillingRunForm(props: BillingRunFormProps) {
  const { billingRunId, namespace: ns = "" } = props;
  const livestockSaleOptions = useSelector(selectSaleOptionsV2);

  const ruleBookOptions = useSelector(selectRuleBookOptions);

  const [{ value: status }] = useField(withNamespace(ns, "status"));
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const [_ignored, _ignored2, { setValue: setName }] = useField(
    withNamespace(ns, "name"),
  );
  const [{ value: livestockSaleIds = [] }] = useField<number[]>(
    withNamespace(ns, "livestockSaleIds"),
  );
  const [{ value: exportedAtDateTime }] = useField(
    withNamespace(ns, "exportedAtDateTime"),
  );

  function updateBillingRunName(
    newLivestockSaleIds: number[],
    newRuleBookId: string,
  ) {
    const ruleBookName =
      flattenOptions(ruleBookOptions as SelectFieldOption[]).find(
        option => option.value === newRuleBookId,
      )?.label || "";

    const livestockSaleNames = livestockSaleOptions
      .filter(option => newLivestockSaleIds.includes(option.value))
      .map(option => option.label)
      .join(", ");

    setName(`${ruleBookName} - ${livestockSaleNames}`);
  }

  function onAfterRuleBookIdChanged(newRuleBookId: string) {
    updateBillingRunName(livestockSaleIds, newRuleBookId);
  }

  const isEdit = Boolean(billingRunId);

  const isRuleBookEnabled = !isEdit;
  const isStatusEnabled = isEdit;
  const isExportedAtVisible =
    isEdit &&
    status === BillingRunStatus.EXPORTED &&
    exportedAtDateTime !== null;

  // If this is editing - pull the definite right settings
  // If this is a creation - pull the default.
  // These should be one and the same in practice.
  const existingBillingSettings = useSelector(
    getBillingSettingsByBillingRunId(billingRunId),
  ) as BillingSettings | null;
  const defaultBillingSettings = useSelector(getDefaultBillingSettings);
  const billingSettings: BillingSettings | null =
    existingBillingSettings || defaultBillingSettings;

  const multipleBankingDetails =
    billingSettings.paymentBankingDetails?.length > 1;

  const featureBuyerPaysVendor = billingSettings?.featureBuyerPaysVendor;

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <SelectField
          disabled={!isRuleBookEnabled}
          label="Rulebook"
          menuPortalTarget={document.body}
          name={withNamespace(ns, "ruleBookId")}
          onChangeExtra={onAfterRuleBookIdChanged}
          options={ruleBookOptions as SelectFieldOption[]}
        />
      </Grid>
      <Grid item sm={12}>
        <Input
          label="Name"
          name={withNamespace(ns, "name")}
          required
          isClearable
        />
      </Grid>
      <Grid item xs={6}>
        <SelectField
          disabled={!isStatusEnabled}
          label="Status"
          name={withNamespace(ns, "status")}
          options={BillingRunStatusOptions}
          required
          menuPortalTarget={document.body}
        />
      </Grid>
      <Grid item xs={6}>
        {isExportedAtVisible && (
          <DateInput
            disabled
            name={withNamespace(ns, "exportedAtDateTime")}
            label="Exported At"
          />
        )}
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>

      <Grid item xs={6}>
        <Input
          beforeSymbol={
            billingSettings?.accountSalePrefix
              ? `${billingSettings.accountSalePrefix}-`
              : BillingNumberPrefixes.RCTI
          }
          label="First Account Sale/RCTI Number"
          name={withNamespace(ns, "nextAccountSaleNumber")}
          disabled
          tooltip="The first value to assign to an Account Sale/RCTI. Subsequent Account Sale/RCTIs will increment by 1."
          type="number"
          overrideValue={billingSettings?.nextAccountSaleNumber}
        />
      </Grid>
      <Grid item xs={6}>
        <Input
          beforeSymbol={
            billingSettings?.invoicePrefix
              ? `${billingSettings.invoicePrefix}-`
              : BillingNumberPrefixes.Invoice
          }
          label="First Invoice Number"
          name={withNamespace(ns, "nextInvoiceNumber")}
          disabled
          overrideValue={billingSettings?.nextInvoiceNumber}
          tooltip="The first value to assign to an Invoice. Subsequent Invoices will increment by 1."
          type="number"
        />
      </Grid>
      {multipleBankingDetails && (
        <Grid item xs={6}>
          <BankingDetailField
            name="paymentBankingDetailsId"
            namespace={ns}
            label="Bank Account for Payments"
            placeholder="Use default"
            tooltip="Allows overriding which Bank Account is shown on an Invoice for receiving Payments."
          />
        </Grid>
      )}
      {featureBuyerPaysVendor && (
        <Grid item xs={6}>
          <CheckBox
            name={withNamespace(ns, "buyerPaysVendor")}
            label="Buyer Pays Vendor"
            tooltip="When enabled, the vendor's bank account is used as the payment destination on invoices instead of the agency default."
          />
        </Grid>
      )}
      <Grid item xs={12}>
        <HelpText>
          Document Prefixes & Numbers are managed in your{" "}
          <Link to={getSettingsRoute("Agency")}>Agency Settings</Link> page.
          Documents will be assigned incrementing numbers upon approval.
        </HelpText>
      </Grid>
    </Grid>
  );
}
